/**
 * API Client - HTTP client per chiamate al backend FastAPI
 *
 * Gestisce:
 * - Base URL configurabile tramite variabile d'ambiente
 * - Gestione automatica token JWT (Authorization header)
 * - Gestione errori centralizzata
 * - Refresh automatico token su 401 Unauthorized
 * - Supporto per GET, POST, PUT, DELETE
 * - Parsing automatico JSON response
 */
import type { TokenResponse } from "@/types/api/auth";
import { storage } from "./local-storage";

// Costruisci API_BASE_URL dalle variabili d'ambiente o usa fallback
const getApiBaseUrl = (): string => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
    const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
    // Rimuovi trailing slash da baseUrl e leading slash da versionPrefix se necessario
    const cleanBaseUrl = baseUrl.replace(/\/$/, "");
    const cleanVersionPrefix = versionPrefix.startsWith("/") ? versionPrefix : `/${versionPrefix}`;
    return `${cleanBaseUrl}${cleanVersionPrefix}`;
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Costruisce un URL completo combinando API_BASE_URL con l'endpoint
 * Gestisce correttamente gli endpoint che iniziano con "/"
 */
function buildApiUrl(endpoint: string): URL {
    // Rimuovi il leading slash dall'endpoint se presente
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    // Assicurati che API_BASE_URL termini con / e che cleanEndpoint non inizi con /
    const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL : `${API_BASE_URL}/`;
    // Costruisci l'URL completo concatenando baseUrl e endpoint
    const fullUrl = `${baseUrl}${cleanEndpoint}`;
    return new URL(fullUrl);
}

/**
 * Gestione refresh token automatico
 * - refreshPromise: Promise del refresh in corso (null se non c'è refresh attivo)
 * - pendingRequests: Coda di richieste in attesa del refresh completato
 */
let refreshPromise: Promise<TokenResponse | null> | null = null;
interface PendingRequest<T> {
    resolve: (value: T) => void;
    reject: (error: unknown) => void;
    endpoint: string;
    options: ApiRequestOptions;
}
const pendingRequests: PendingRequest<unknown>[] = [];

export interface ApiError {
    detail: string;
    status: number;
}

export class ApiClientError extends Error {
    constructor(
        public status: number,
        public detail: string,
        public response?: Response,
    ) {
        super(detail);
        this.name = "ApiClientError";
    }
}

/**
 * Ottiene il token JWT dal localStorage
 */
function getAuthToken(): string | null {
    return storage.getItem("access_token");
}

/**
 * Salva il token JWT nel localStorage
 */
export function setAuthToken(token: string): void {
    storage.setItem("access_token", token);
    // Trigger custom event for other components to react to token change
    window.dispatchEvent(new CustomEvent("tokenChanged"));
}

/**
 * Rimuove il token JWT dal localStorage
 */
export function removeAuthToken(): void {
    storage.removeItems(["access_token", "refresh_token"]);
}

/**
 * Ottiene il refresh token dal localStorage
 */
function getRefreshToken(): string | null {
    return storage.getItem("refresh_token");
}

/**
 * Funzione sincrona per refresh del token access
 * Chiama direttamente l'endpoint /auth/refresh senza usare React Query
 *
 * @returns Promise<TokenResponse> se il refresh riesce, null se non c'è refresh_token disponibile
 * @throws ApiClientError se il refresh fallisce
 */
export async function refreshAccessToken(): Promise<TokenResponse | null> {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
        console.warn("[api-client] No refresh token available");
        return null;
    }

    try {
        const url = buildApiUrl("/auth/refresh");
        console.log("[api-client] Calling refresh endpoint:", url.toString());
        const response = await fetch(url.toString(), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            console.error("[api-client] Refresh failed:", response.status, errorText);
            // Se il refresh fallisce, rimuovi i token
            removeAuthToken();
            throw new ApiClientError(response.status, `Token refresh failed: ${errorText}`, response);
        }

        const tokenResponse: TokenResponse = await response.json();
        console.log("[api-client] Refresh successful, saving new tokens");

        // Salva i nuovi token
        setAuthToken(tokenResponse.access_token);
        storage.setItem("refresh_token", tokenResponse.refresh_token);

        return tokenResponse;
    } catch (error) {
        console.error("[api-client] Refresh error:", error);
        // Se c'è un errore di rete o altro, rimuovi i token
        removeAuthToken();
        throw error;
    }
}

/**
 * Gestisce gli errori HTTP
 * Nota: La gestione del 401 con refresh automatico è gestita in apiClient()
 * Questa funzione viene chiamata solo dopo che il refresh è fallito o non disponibile
 */
async function handleError(response: Response, skipLogout = false): Promise<never> {
    let detail = `HTTP ${response.status}: ${response.statusText}`;

    try {
        const errorData = await response.json();
        if (errorData.detail) {
            detail = typeof errorData.detail === "string" ? errorData.detail : JSON.stringify(errorData.detail);
        }
    } catch {
        // Se non riesce a parsare JSON, usa il testo della risposta
        try {
            const text = await response.text();
            if (text) detail = text;
        } catch {
            // Ignora errori di parsing
        }
    }

    // Logout automatico su 401 Unauthorized (solo se skipLogout è false)
    // skipLogout è true quando il refresh è già stato gestito in apiClient()
    if (response.status === 401 && !skipLogout) {
        removeAuthToken();
        // Emetti evento per notificare il logout
        window.dispatchEvent(new CustomEvent("auth:logout"));
    }

    throw new ApiClientError(response.status, detail, response);
}

/**
 * Opzioni per le chiamate API
 */
export interface ApiRequestOptions extends RequestInit {
    requireAuth?: boolean;
    params?: Record<string, string | number | boolean | null | undefined>;
    signal?: AbortSignal;
}

/**
 * Esegue una singola richiesta HTTP
 * Funzione helper per evitare duplicazione di codice
 */
async function executeRequest(endpoint: string, options: ApiRequestOptions): Promise<Response> {
    const { requireAuth = true, params, headers = {}, ...fetchOptions } = options;

    // Costruisci URL con query params
    const url = buildApiUrl(endpoint);
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                url.searchParams.append(key, String(value));
            }
        });
    }

    // Prepara headers
    // Non aggiungere Content-Type se il body è FormData (il browser lo imposterà automaticamente con il boundary)
    const requestHeaders: Record<string, string> = {};
    if (!(fetchOptions.body instanceof FormData)) {
        requestHeaders["Content-Type"] = "application/json";
    }
    Object.assign(requestHeaders, headers as Record<string, string>);

    // Aggiungi token JWT se richiesto
    if (requireAuth) {
        const token = getAuthToken();
        if (token) {
            requestHeaders["Authorization"] = `Bearer ${token}`;
        }
    }

    // Esegui richiesta
    return await fetch(url.toString(), {
        ...fetchOptions,
        headers: requestHeaders,
    });
}

/**
 * Client API base con gestione refresh automatico token
 */
export async function apiClient<T = unknown>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const { requireAuth = true } = options;

    // Non fare refresh per endpoint /auth/refresh stesso (evita loop infiniti)
    const isRefreshEndpoint = endpoint === "/auth/refresh" || endpoint.startsWith("/auth/refresh?");

    // Non fare refresh per richieste senza autenticazione
    const shouldAttemptRefresh = requireAuth && !isRefreshEndpoint;

    // Esegui richiesta iniziale
    let response = await executeRequest(endpoint, options);

    // Gestione 401 con refresh automatico
    if (!response.ok && response.status === 401 && shouldAttemptRefresh) {
        const refreshToken = getRefreshToken();

        // Se c'è un refresh_token disponibile, tenta il refresh
        if (refreshToken) {
            // Se c'è già un refresh in corso, aggiungi questa richiesta alla coda
            if (refreshPromise) {
                return new Promise<T>((resolve, reject) => {
                    pendingRequests.push({
                        resolve: (value: unknown) => resolve(value as T),
                        reject,
                        endpoint,
                        options,
                    });
                });
            }

            // Crea Promise per questa richiesta e aggiungi alla coda
            return new Promise<T>((resolve, reject) => {
                const currentRequest: PendingRequest<unknown> = {
                    resolve: (value: unknown) => resolve(value as T),
                    reject,
                    endpoint,
                    options,
                };
                pendingRequests.push(currentRequest);

                // Avvia refresh (imposta refreshPromise immediatamente per evitare race conditions)
                refreshPromise = refreshAccessToken()
                    .then((tokenResponse) => {
                        // Se refreshAccessToken restituisce null, significa che non c'era refresh_token
                        // Questo non dovrebbe accadere perché abbiamo già controllato, ma gestiamolo
                        if (!tokenResponse) {
                            throw new ApiClientError(401, "No refresh token available");
                        }
                        return tokenResponse;
                    })
                    .then(async (tokenResponse) => {
                        // Refresh riuscito
                        refreshPromise = null;

                        // Riprova tutte le richieste in coda
                        const requestsToRetry = [...pendingRequests];
                        pendingRequests.length = 0;

                        // Riprova tutte le richieste
                        await Promise.allSettled(
                            requestsToRetry.map(async (req) => {
                                try {
                                    const retryResponse = await executeRequest(req.endpoint, req.options);

                                    if (!retryResponse.ok) {
                                        // Se la riprova fallisce, gestisci errore normalmente (senza logout)
                                        // perché il refresh è già riuscito
                                        try {
                                            await handleError(retryResponse, true);
                                        } catch (error) {
                                            req.reject(error);
                                        }
                                        return;
                                    }

                                    // Parsa risposta
                                    if (retryResponse.status === 204) {
                                        req.resolve(undefined as T);
                                    } else {
                                        try {
                                            const data = await retryResponse.json();
                                            req.resolve(data as T);
                                        } catch (error) {
                                            req.reject(new ApiClientError(retryResponse.status, "Invalid JSON response", retryResponse));
                                        }
                                    }
                                } catch (error) {
                                    req.reject(error);
                                }
                            }),
                        );

                        return tokenResponse;
                    })
                    .catch((error) => {
                        // Refresh fallito
                        refreshPromise = null;

                        // Rifiuta tutte le richieste in coda (inclusa quella corrente)
                        const requestsToReject = [...pendingRequests];
                        pendingRequests.length = 0;

                        requestsToReject.forEach((req) => {
                            req.reject(error);
                        });

                        // Rimuovi token e emetti evento logout
                        removeAuthToken();
                        window.dispatchEvent(new CustomEvent("auth:logout"));

                        throw error;
                    });
            });
        } else {
            console.warn("[api-client] 401 received but no refresh token available");
        }
    }

    // Gestisci errori HTTP (dopo eventuale refresh)
    if (!response.ok) {
        // skipLogout è true se abbiamo già gestito il refresh
        await handleError(response, shouldAttemptRefresh && !!getRefreshToken());
    }

    // Gestisci risposte vuote (204 No Content)
    if (response.status === 204) {
        return undefined as T;
    }

    // Parsa JSON response
    try {
        return await response.json();
    } catch (error) {
        throw new ApiClientError(response.status, "Invalid JSON response", response);
    }
}

/**
 * GET request
 */
export function apiGet<T = unknown>(endpoint: string, options?: Omit<ApiRequestOptions, "method" | "body">): Promise<T> {
    return apiClient<T>(endpoint, { ...options, method: "GET" });
}

/**
 * POST request
 */
export function apiPost<T = unknown>(endpoint: string, data?: unknown, options?: Omit<ApiRequestOptions, "method">): Promise<T> {
    const { body, ...restOptions } = options || {};

    return apiClient<T>(endpoint, {
        ...restOptions,
        method: "POST",
        body: body || (data ? JSON.stringify(data) : undefined),
    });
}

/**
 * PUT request
 */
export function apiPut<T = unknown>(endpoint: string, data?: unknown, options?: Omit<ApiRequestOptions, "method">): Promise<T> {
    const { body, ...restOptions } = options || {};

    return apiClient<T>(endpoint, {
        ...restOptions,
        method: "PUT",
        body: body || (data ? JSON.stringify(data) : undefined),
    });
}

/**
 * DELETE request
 */
export function apiDelete<T = unknown>(endpoint: string, options?: Omit<ApiRequestOptions, "method" | "body">): Promise<T> {
    return apiClient<T>(endpoint, { ...options, method: "DELETE" });
}

/**
 * DELETE request with body
 */
export function apiDeleteWithBody<T = unknown>(endpoint: string, data?: unknown, options?: Omit<ApiRequestOptions, "method">): Promise<T> {
    const { body, ...restOptions } = options || {};

    return apiClient<T>(endpoint, {
        ...restOptions,
        method: "DELETE",
        body: body || (data ? JSON.stringify(data) : undefined),
    });
}

/**
 * PATCH request
 */
export function apiPatch<T = unknown>(endpoint: string, data?: unknown, options?: Omit<ApiRequestOptions, "method">): Promise<T> {
    const { body, ...restOptions } = options || {};

    return apiClient<T>(endpoint, {
        ...restOptions,
        method: "PATCH",
        body: body || (data ? JSON.stringify(data) : undefined),
    });
}

/**
 * POST request con FormData (per upload file)
 */
export function apiPostFormData<T = unknown>(endpoint: string, formData: FormData, options?: Omit<ApiRequestOptions, "method" | "body">): Promise<T> {
    const { headers: optionsHeaders, ...restOptions } = options || {};

    // Rimuovi Content-Type per permettere al browser di impostare il boundary
    const requestHeaders: Record<string, string> = {
        ...(optionsHeaders as Record<string, string> | undefined),
    };
    delete requestHeaders["Content-Type"];

    return apiClient<T>(endpoint, {
        ...restOptions,
        method: "POST",
        body: formData,
        headers: requestHeaders,
    });
}

/**
 * GET request che restituisce un Blob (per file binari come PDF, immagini, etc.)
 * Gestisce automaticamente autenticazione e refresh del token
 */
export async function apiGetBlob(endpoint: string, options?: Omit<ApiRequestOptions, "method" | "body">): Promise<Blob> {
    const { requireAuth = true } = options || {};

    // Non fare refresh per endpoint /auth/refresh stesso (evita loop infiniti)
    const isRefreshEndpoint = endpoint === "/auth/refresh" || endpoint.startsWith("/auth/refresh?");

    // Non fare refresh per richieste senza autenticazione
    const shouldAttemptRefresh = requireAuth && !isRefreshEndpoint;

    // Esegui richiesta iniziale
    let response = await executeRequest(endpoint, { ...options, method: "GET" });

    // Gestione 401 con refresh automatico
    if (!response.ok && response.status === 401 && shouldAttemptRefresh) {
        const refreshToken = getRefreshToken();

        // Se c'è un refresh_token disponibile, tenta il refresh
        if (refreshToken) {
            // Se c'è già un refresh in corso, aspetta e riprova
            if (refreshPromise) {
                await refreshPromise;
                // Riprova la richiesta dopo il refresh
                response = await executeRequest(endpoint, { ...options, method: "GET" });
            } else {
                // Avvia refresh
                try {
                    refreshPromise = refreshAccessToken();
                    await refreshPromise;
                    refreshPromise = null;
                    // Riprova la richiesta dopo il refresh
                    response = await executeRequest(endpoint, { ...options, method: "GET" });
                } catch (error) {
                    // Refresh fallito
                    refreshPromise = null;
                    removeAuthToken();
                    window.dispatchEvent(new CustomEvent("auth:logout"));
                    throw error;
                }
            }
        } else {
            console.warn("[api-client] 401 received but no refresh token available");
        }
    }

    // Gestisci errori HTTP (dopo eventuale refresh)
    if (!response.ok) {
        await handleError(response, shouldAttemptRefresh && !!getRefreshToken());
    }

    // Restituisci blob
    return await response.blob();
}
